import moment from 'moment';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import usePdfDialogHook from '../../../../redux/hooks/usePdfDialogHook';
import { defaultFrom } from '../../../options';
import Card from '../../cards/Card';
import PrintCardsContainer from '../../cards/PrintCardsContainer';
import PageHeader from '../../page/PageHeader';
import PDFDialog from '../../modals/PDFDialog';
import SInput from '../SInput';
import useUrlParams from '../../../hooks/useUrlParams';
import { QTYButtons } from '../QtyPrintComponent';
import { isNullOrWhitespace } from '../../../helpers/string-helper';

// The form is very similar in box label and production hold label
type UseBoxLabelAndProductionHoldFormType = {
  title: string;
  apiUrl: string;
  apiOnHoldUrl?: string;
  printText: string;

  isBoxLabelGenerator?: boolean;
  isProductionHold?: boolean;
};
const useBoxLabelAndProductionHoldForm = (input: UseBoxLabelAndProductionHoldFormType) => {
  const { canGenerate, isPDFDialogOpen, setIsPDFDialogOpen, pdfBase64, print } = usePdfDialogHook();
  const {
    batchID,
    setBatchID,

    shipmentID,
    setShipmentID,
    shipmentName,
    setShipmentName,
    destinationFC,
    setDestinationFC,
    destinationAddress,
    setDestinationAddress,
    isShipmentOnHold,

    productSku,
    productFNSku,
    productName,
    productCondition,
    productID,
    setProductID,
    productQty,
    setProductQty,
    productImage,
    productExpiration,
    setProductExpiration,

    printQty,
    setPrintQty,
    printFnSkuOnLabel,
    setPrintFnSkuOnLabel,
    boxStartCount
  } = useUrlParams();
  const showHoldButtons = isShipmentOnHold && input.isBoxLabelGenerator;
  const _apiUrl = input.apiUrl;
  const _apiOnHoldUrl = input.apiOnHoldUrl;

  const printBoxLabel = async (apiUrl: string) => {
    // Production hold doesn't require shipment info
    if (!input.isProductionHold) {
      if (!(shipmentName && shipmentID)) {
        toast.warn('Missing shipment inputs.');
        return;
      }
    }

    if (printQty > 0 && productQty > 0 && !isNullOrWhitespace(productName)) {
      const exp =
        productExpiration == null ? '' : `,EXP:${moment(productExpiration).format('yyyy/MM/DD')}`;

      await print({
        apiUrl,
        data: {
          numberOfBoxes: printQty,
          shipmentID,
          productID,
          productName,
          productFNSku,
          productCondition,
          shipmentName,
          shipmentCode: shipmentID,
          from: defaultFrom,
          to: destinationAddress,
          boxContentCode417: `AMZN,PO:${shipmentID},FNSKU:${productFNSku},QTY:${productQty}${exp}`,
          content: `FNSKU:${productFNSku}`,
          qty: productQty,
          boxStartCount,

          productExpiration:
            productExpiration == null ? '' : moment(productExpiration).format('LL'),
          printfnsku: printFnSkuOnLabel,
          batchID: batchID,
          printedTimestamp: moment().format('MMMM DD, YYYY h:mm A')
        }
      });
    } else toast.warn('Missing inputs');
  };

  const printOnHoldBoxLabel = async (onHoldLabelType: '3x2' | '2x1') => {
    if (isNullOrWhitespace(_apiOnHoldUrl)) {
      toast.warn('On hold url is missing.');
      return;
    }

    if (printQty > 0 && productQty > 0 && !isNullOrWhitespace(productName)) {
      await print({
        apiUrl: _apiOnHoldUrl!,
        data: {
          onHoldLabelType,
          productSku,
          productName,
          qty: productQty,
          numberOfBoxes: printQty,
          printedTimestamp: moment().format('MMMM DD, YYYY h:mm A')
        }
      });
    } else toast.warn('Missing inputs');
  };

  const clearProduct = () => {
    setProductID('');
    setProductQty(1);
    setProductExpiration(null);
    setPrintFnSkuOnLabel(false);
  };

  const form = (
    <div className="flex flex-col w-full">
      <PageHeader headingText={input.title} />

      <PrintCardsContainer
        leftCard={
          <Card headerText="1. Shipment ID & Destination Info">
            <p className="font-normal bg-red-500"></p>
            <div className="mt-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex justify-center">
                  <SInput name="Batch ID" value={batchID} onChange={setBatchID}></SInput>
                </div>
                <div className="flex justify-center">
                  <SInput name="Shipment ID" value={shipmentID} onChange={setShipmentID} />
                </div>
              </div>
            </div>
            <div className="grid grid-flow-row gap-5 mt-5 md:grid-cols-2 sm:grid-cols-1">
              <SInput name="Shipment Name" value={shipmentName} onChange={setShipmentName} />
              <SInput name="Destination FC" value={destinationFC} onChange={setDestinationFC} />
            </div>
            <div className="grid grid-flow-row grid-cols-1 gap-5 mt-5">
              <SInput
                name="Destination Address"
                value={destinationAddress}
                onChange={setDestinationAddress}
              />
            </div>
          </Card>
        }
        rightCard={
          <Card headerText="Print">
            <div className="flex flex-col gap-3">
              {/* On hold has different print options */}
              {showHoldButtons && (
                <>
                  <button
                    disabled={!canGenerate}
                    onClick={() => {
                      printOnHoldBoxLabel('3x2');
                    }}
                    className="w-full base-green-button"
                  >
                    Print 3x2 On Hold Label
                  </button>
                  <button
                    disabled={!canGenerate}
                    onClick={() => {
                      printOnHoldBoxLabel('2x1');
                    }}
                    className="w-full base-green-button"
                  >
                    Print 2-¼ x 1-¼ On Hold Label
                  </button>
                </>
              )}

              {!showHoldButtons && (
                <>
                  <button
                    disabled={!canGenerate}
                    onClick={() => {
                      printBoxLabel(_apiUrl);
                    }}
                    className="w-full base-green-button"
                  >
                    {input.printText}
                  </button>
                  <button
                    disabled={!canGenerate}
                    onClick={clearProduct}
                    className="w-full base-red-button"
                  >
                    Clear SKU
                  </button>
                </>
              )}

              <div className="flex flex-col px-3">
                <h6 className="flex font-bold text dark:text-white">{productFNSku}</h6>
                <h6 className="flex text-sm dark:text-white">{productName}</h6>
                <div className="flex items-center justify-center w-full border rounded max-h-96 dark:bg-gray-700">
                  {productImage ? (
                    <img
                      alt="selected product"
                      src={productImage}
                      className="w-auto h-auto mt-2 max-h-28"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </Card>
        }
      />

      <Card headerText="2. Items in the Box">
        <div className="flex flex-wrap">
          <div className="flex flex-col w-1/2 px-3">
            <div className="flex gap-2">
              <div className="flex w-56">
                <SInput name="Item ID" value={productID} onChange={setProductID} />
              </div>

              <div className="flex flex-col flex-wrap gap-3">
                <p className="text-sm font-medium base-p">Select Number of Units in Box</p>
                <div className="flex flex-row flex-wrap gap-2 -mt-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 16, 18, 20, 24, 32].map((x) => (
                    <button
                      className="base-gray-button"
                      key={`unit_` + x}
                      onClick={() => setProductQty(x)}
                    >
                      {x}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-flow-row gap-5 mt-5 sm:grid-cols-1 md:grid-cols-3">
              <SInput name="Quantity" value={productQty} onChange={setProductQty} type="number" />
              <div className="w-full">
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                  Expiration Date
                </label>
                <DatePicker
                  className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
                  selected={productExpiration}
                  onChange={(date: Date) => setProductExpiration(date)}
                />
              </div>
              <div className="flex items-center">
                <input
                  id="printsku-checkbox"
                  type="checkbox"
                  checked={printFnSkuOnLabel}
                  onChange={(e) => setPrintFnSkuOnLabel(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                ></input>
                <label
                  htmlFor="printsku-checkbox"
                  className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                >
                  Print FNSKU on Label
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-col w-1/2 px-3">
            <div
              className="flex justify-center p-2 text-white bg-black rounded-lg dark:bg-gray-900"
              role="alert"
            >
              <div className="flex items-center">
                <div>
                  <div className="font-medium">Select Number of Identical Boxes</div>
                </div>
              </div>
            </div>

            <QTYButtons printQty={printQty} setPrintQty={setPrintQty} />
          </div>
        </div>

        <PDFDialog isOpen={isPDFDialogOpen} setIsOpen={setIsPDFDialogOpen} pdf={pdfBase64} />
      </Card>
    </div>
  );

  return {
    form
  };
};

export default useBoxLabelAndProductionHoldForm;
